import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X, Trash2, Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { usePermissions } from '@/hooks/use-permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to get current date in Pakistan timezone (UTC+5)
const getPakistanDate = () => {
  const now = new Date();
  // Convert to Pakistan time by adding 5 hours to UTC
  const pakistanTime = new Date(now.getTime() + (5 * 60 * 60 * 1000));
  return pakistanTime.toISOString().split('T')[0];
};

export default function PurchaseOrders({ user, onLogout }) {
  const navigate = useNavigate();
  const { hasPermission, canCreate, canEdit, canDelete, canApprove } = usePermissions(user);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewDetails, setViewDetails] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('PENDING'); // Default to PENDING
  const [editingPO, setEditingPO] = useState(null); // Track if we're editing
  const [executeDialogOpen, setExecuteDialogOpen] = useState(false); // Execute confirmation dialog
  const [poToExecute, setPoToExecute] = useState(null); // PO being executed
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false); // Delete confirmation dialog
  const [poToDelete, setPoToDelete] = useState(null); // PO being deleted
  const [statusAuthDialogOpen, setStatusAuthDialogOpen] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [searchTerm, setSearchTerm] = useState(''); // Search by PO number
  const [categoryFilter, setCategoryFilter] = useState(''); // Filter by raw good type
  
  // Form state
  const [poRefNumber, setPoRefNumber] = useState('');
  const [date, setDate] = useState('');
  const [rawGoodType, setRawGoodType] = useState('');
  const [fund, setFund] = useState('');
  const [items, setItems] = useState([]);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');

  const fundOptions = [
    { value: 'petty_cash', label: 'Petty Cash' },
    { value: 'finance_department', label: 'Finance Department' }
  ];

  const getFundStatusOptions = (fund) => {
    if (fund === 'petty_cash') {
      return [
        { value: 'APPROVED', label: 'Approved' },
        { value: 'REJECTED', label: 'Rejected' }
      ];
    } else if (fund === 'finance_department') {
      return [
        { value: 'AUTHORIZED', label: 'Authorized' },
        { value: 'DENIED', label: 'Denied' }
      ];
    }
    // Default to Petty Cash options for old POs
    return [
      { value: 'APPROVED', label: 'Approved' },
      { value: 'REJECTED', label: 'Rejected' }
    ];
  };

  const getAuthCodeForFund = (fund) => {
    if (fund === 'petty_cash') {
      return 'poaprden';
    } else if (fund === 'finance_department') {
      return '1436';
    }
    // Default to poaprden for old POs
    return 'poaprden';
  };
  
  // Item form state
  const [showItemForm, setShowItemForm] = useState(false);
  const [currentItem, setCurrentItem] = useState({
    code: '',
    color_code: '',
    color_name: '',
    quantity: '',
    unit_price: '',
    total_price: 0
  });
  
  // Master data
  const [availableCodes, setAvailableCodes] = useState([]);
  const [availableColors, setAvailableColors] = useState([]);

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  const fetchPurchaseOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/purchase-orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchaseOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch purchase orders:', error);
    }
  };

  const handleOpenForm = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch next PO number
      const poNumberResponse = await axios.get(`${API}/purchase-orders/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPoRefNumber(poNumberResponse.data.po_ref_number);
      setDate(getPakistanDate());
      setRawGoodType('');
      setFund('');
      setItems([]);
      setExpectedDeliveryDate('');
      setErrorMessage('');
      setEditingPO(null); // Not editing, creating new
      setOpen(true);
    } catch (error) {
      console.error('Error opening form:', error);
      toast.error('Failed to initialize form');
    }
  };

  const handleRawGoodTypeChange = async (value) => {
    setRawGoodType(value);
    setItems([]); // Clear items when changing raw good type
    setShowItemForm(false);
    
    // Fetch codes for the selected module
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/codes/${value}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableCodes(response.data);
    } catch (error) {
      console.error('Error fetching codes:', error);
      toast.error('Failed to fetch codes');
    }
  };

  const handleCodeChange = async (value) => {
    setCurrentItem({ ...currentItem, code: value, color_code: '', color_name: '', unit_price: '' });
    
    // Fetch colors for the selected code
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/colors/${rawGoodType}/${value}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableColors(response.data);
    } catch (error) {
      console.error('Error fetching colors:', error);
      toast.error('Failed to fetch colors');
    }
  };

  const handleColorChange = (value) => {
    const selectedColor = availableColors.find(c => c.color_code === value);
    if (selectedColor) {
      // For fabrics, use price_per_meter; for others, use price
      const price = rawGoodType === 'FABRIC LN' ? (selectedColor.price_per_meter || 0) : (selectedColor.price || 0);
      setCurrentItem({
        ...currentItem,
        color_code: selectedColor.color_code,
        color_name: selectedColor.color_name,
        unit_price: price
      });
    }
  };

  const handleQuantityChange = (e) => {
    const quantity = Math.round(parseFloat(e.target.value)) || 0;
    const unitPrice = parseFloat(currentItem.unit_price) || 0;
    setCurrentItem({
      ...currentItem,
      quantity: quantity,
      total_price: quantity * unitPrice
    });
  };

  const handleAddItem = () => {
    if (!currentItem.code || !currentItem.color_code || !currentItem.quantity || parseFloat(currentItem.quantity) <= 0) {
      toast.error('Please fill all item fields');
      return;
    }
    
    setItems([...items, { ...currentItem }]);
    setCurrentItem({
      code: '',
      color_code: '',
      color_name: '',
      quantity: '',
      unit_price: '',
      total_price: 0
    });
    setShowItemForm(false);
    toast.success('Item added successfully');
  };

  const handleRemoveItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
    toast.success('Item removed');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    
    if (!fund) {
      setErrorMessage('Please select a fund');
      return;
    }
    
    if (items.length === 0) {
      setErrorMessage('Please add at least one item to the purchase order');
      return;
    }
    
    if (!expectedDeliveryDate) {
      setErrorMessage('Please select an expected delivery date');
      return;
    }
    
    // Validate expected delivery date is in the future (Pakistan time)
    const pakistanToday = getPakistanDate();
    const deliveryDate = new Date(expectedDeliveryDate);
    const todayDate = new Date(pakistanToday);
    
    if (deliveryDate < todayDate) {
      setErrorMessage('Expected delivery date cannot be in the past');
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const payload = {
        raw_good_type: rawGoodType,
        fund: fund,
        items: items,
        expected_delivery_date: expectedDeliveryDate
      };
      
      if (editingPO) {
        // Update existing PO
        await axios.put(`${API}/purchase-orders/${editingPO.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase Order updated successfully');
      } else {
        // Create new PO
        await axios.post(`${API}/purchase-orders`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Purchase Order created successfully');
      }
      
      setOpen(false);
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error saving PO:', error);
      setErrorMessage(error.response?.data?.detail || 'Failed to save purchase order');
    }
  };

  const handleDelete = async (po) => {
    setPoToDelete(po);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!poToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/purchase-orders/${poToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Purchase order deleted successfully');
      setDeleteDialogOpen(false);
      setPoToDelete(null);
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error deleting PO:', error);
      toast.error('Failed to delete purchase order');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setPoToDelete(null);
  };

  const handleExecute = async (po) => {
    setPoToExecute(po);
    setExecuteDialogOpen(true);
  };

  const confirmExecute = async () => {
    if (!poToExecute) return;
    
    try {
      const token = localStorage.getItem('token');
      
      // Update status to EXECUTED
      await axios.patch(`${API}/purchase-orders/${poToExecute.id}/status`, {
        status: 'EXECUTED'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Purchase Order ${poToExecute.po_ref_number} executed successfully!`);
      setExecuteDialogOpen(false);
      setPoToExecute(null);
      fetchPurchaseOrders();
      // TODO: Add actual execution logic when inventory module is ready
    } catch (error) {
      console.error('Error executing PO:', error);
      toast.error('Failed to execute purchase order');
    }
  };

  const cancelExecute = () => {
    setExecuteDialogOpen(false);
    setPoToExecute(null);
  };

  const handleViewPO = async (po) => {
    // Fetch vendor information ONLY (keep historical prices - POs are orders at specific prices)
    try {
      const token = localStorage.getItem('token');
      const enrichedItems = await Promise.all(po.items.map(async (item) => {
        try {
          // Fetch the master data entry for this code to get vendor info
          const masterDataResponse = await axios.get(
            `${API}/master-data/by-code/${po.raw_good_type}/${item.code}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          let enrichedItem = { ...item };
          
          if (masterDataResponse.data) {
            const masterData = masterDataResponse.data;
            
            // DON'T update prices - POs represent historical orders at specific prices
            // ONLY fetch vendor details if vendor_name exists
            if (masterData.vendor_name) {
              try {
                const vendorResponse = await axios.get(
                  `${API}/vendors/by-name/${encodeURIComponent(masterData.vendor_name)}`,
                  { headers: { Authorization: `Bearer ${token}` } }
                );
                enrichedItem.vendor_name = vendorResponse.data.vendor_name || 'N/A';
                enrichedItem.local_or_imported = vendorResponse.data.local_or_imported || 'N/A';
                enrichedItem.country = vendorResponse.data.country || 'N/A';
              } catch (vendorError) {
                console.error('Error fetching vendor:', vendorError);
                enrichedItem.vendor_name = 'N/A';
                enrichedItem.local_or_imported = 'N/A';
                enrichedItem.country = 'N/A';
              }
            } else {
              enrichedItem.vendor_name = 'N/A';
              enrichedItem.local_or_imported = 'N/A';
              enrichedItem.country = 'N/A';
            }
          }
          
          return enrichedItem;
        } catch (error) {
          console.error('Error enriching item:', item.code, error);
          return { ...item, vendor_name: 'N/A', local_or_imported: 'N/A', country: 'N/A' };
        }
      }));
      
      setViewDetails({ ...po, items: enrichedItems });
      setViewOpen(true);
    } catch (error) {
      console.error('Error in handleViewPO:', error);
      // Fallback to showing PO without enrichment
      setViewDetails(po);
      setViewOpen(true);
    }
  };

  const handleStatusChangeRequest = (newStatus) => {
    setPendingStatusChange(newStatus);
    setStatusAuthDialogOpen(true);
  };

  const handleStatusChange = async (newStatus) => {
    // This function is now called after authorization
    if (!newStatus) newStatus = pendingStatusChange;
    
    // For PENDING, update immediately (no confirmation needed)
    await confirmStatusChange(newStatus);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;

    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API}/purchase-orders/${viewDetails.id}/status`, {
        status: pendingStatusChange,
        rejection_reason: '' // Empty string for rejection reason (not required)
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Update viewDetails with the response from backend to get approved_date/rejected_date
      setViewDetails(response.data);
      toast.success(`Purchase Order ${pendingStatusChange}`);
      fetchPurchaseOrders();
      
      // Close dialog
      setStatusAuthDialogOpen(false);
      setPendingStatusChange(null);
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const cancelStatusChange = () => {
    setStatusAuthDialogOpen(false);
    setPendingStatusChange(null);
    // Reset dropdown to current status
    setViewDetails({ ...viewDetails });
  };

  const handlePrintPO = async (po) => {
    // Fetch vendor information first
    let enrichedPO = po;
    try {
      const token = localStorage.getItem('token');
      const enrichedItems = await Promise.all(po.items.map(async (item) => {
        try {
          const masterDataResponse = await axios.get(
            `${API}/master-data/by-code/${po.raw_good_type}/${item.code}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (masterDataResponse.data && masterDataResponse.data.vendor_name) {
            const vendorResponse = await axios.get(
              `${API}/vendors/by-name/${encodeURIComponent(masterDataResponse.data.vendor_name)}`,
              { headers: { Authorization: `Bearer ${token}` } }
            );
            
            return {
              ...item,
              vendor_name: vendorResponse.data.vendor_name || 'N/A',
              local_or_imported: vendorResponse.data.local_or_imported || 'N/A',
              country: vendorResponse.data.country || 'N/A'
            };
          }
          
          return { ...item, vendor_name: 'N/A', local_or_imported: 'N/A', country: 'N/A' };
        } catch (error) {
          console.error('Error fetching vendor info for print:', item.code, error);
          return { ...item, vendor_name: 'N/A', local_or_imported: 'N/A', country: 'N/A' };
        }
      }));
      
      enrichedPO = { ...po, items: enrichedItems };
    } catch (error) {
      console.error('Error enriching PO for print:', error);
    }
    
    // Create a print-friendly version of the PO
    const printWindow = window.open('', '_blank');
    const grandTotal = enrichedPO.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0) || 0;
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Purchase Order</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 40px;
              color: #333;
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 30px;
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header-text {
              text-align: left;
            }
            .header-text h1 {
              margin: 5px 0 0 0;
              font-size: 18px !important;
              font-weight: normal;
            }
            .header-text p {
              margin: 0;
              color: #333;
              font-size: 26px !important;
              font-weight: bold !important;
            }
            .header-barcode {
              text-align: right;
            }
            .header-barcode img {
              max-width: 250px;
              height: auto;
            }
            .info-section {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
            }
            .info-item {
              margin-bottom: 10px;
            }
            .info-label {
              font-weight: bold;
              color: #666;
              font-size: 12px;
            }
            .info-value {
              font-size: 14px;
              margin-top: 2px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th, td {
              border: 1px solid #ddd;
              padding: 12px;
              text-align: left;
            }
            th {
              background-color: #f3f4f6;
              font-weight: bold;
              font-size: 12px;
              text-transform: uppercase;
            }
            td {
              font-size: 14px;
            }
            .total-row {
              font-weight: bold;
              background-color: #f9fafb;
            }
            .footer {
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              text-align: center;
              color: #666;
              font-size: 12px;
            }
            @media print {
              body {
                padding: 20px;
              }
              @page {
                size: auto;
                margin: 10mm;
              }
            }
            /* Hide browser print headers/footers */
            @page { 
              margin-top: 0;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-text">
              <p>MNTX CITY OFFICE</p>
              <h1>PURCHASE ORDER</h1>
            </div>
            ${enrichedPO.barcode && enrichedPO.barcode.image ? `
            <div class="header-barcode">
              <img src="${enrichedPO.barcode.image}" alt="Barcode" />
            </div>
            ` : ''}
          </div>
          
          <div class="info-section">
            <div>
              <div class="info-item">
                <div class="info-label">PO REF NUMBER</div>
                <div class="info-value">${enrichedPO.po_ref_number}</div>
              </div>
              <div class="info-item">
                <div class="info-label">DATE OF REQUEST</div>
                <div class="info-value">${enrichedPO.date}</div>
              </div>
              ${enrichedPO.approved_date ? `
              <div class="info-item">
                <div class="info-label">DATE OF APPROVAL</div>
                <div class="info-value">${enrichedPO.approved_date.split('T')[0]}</div>
              </div>
              ` : ''}
              ${enrichedPO.executed_date ? `
              <div class="info-item">
                <div class="info-label">DATE OF EXECUTION</div>
                <div class="info-value">${enrichedPO.executed_date.split('T')[0]}</div>
              </div>
              ` : ''}
              <div class="info-item">
                <div class="info-label">STATUS</div>
                <div class="info-value">${enrichedPO.status}</div>
              </div>
            </div>
            <div>
              <div class="info-item">
                <div class="info-label">RAW GOOD TYPE</div>
                <div class="info-value">${enrichedPO.raw_good_type}</div>
              </div>
              ${enrichedPO.items && enrichedPO.items.length > 0 && enrichedPO.items[0].local_or_imported ? `
              <div class="info-item">
                <div class="info-label">LOCAL OR IMPORTED</div>
                <div class="info-value">${enrichedPO.items[0].local_or_imported}</div>
              </div>
              <div class="info-item">
                <div class="info-label">COUNTRY</div>
                <div class="info-value">${enrichedPO.items[0].country}</div>
              </div>
              ` : ''}
              <div class="info-item">
                <div class="info-label">EXPECTED DELIVERY DATE</div>
                <div class="info-value">${enrichedPO.expected_delivery_date}</div>
              </div>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th>CODE</th>
                <th>COLOR CODE</th>
                <th>COLOR</th>
                <th>QUANTITY</th>
                <th>${enrichedPO.raw_good_type === 'FABRIC LN' ? 'PRICE PER METER' : 'UNIT PRICE'}</th>
                <th>TOTAL PRICE</th>
              </tr>
            </thead>
            <tbody>
              ${enrichedPO.items?.map(item => {
                const isFabric = enrichedPO.raw_good_type === 'FABRIC LN';
                const qtyDisplay = isFabric ? `${Math.round(item.quantity)} Meters` : `${Math.round(item.quantity)} Units`;
                const priceDisplay = isFabric ? `$${parseFloat(item.unit_price || 0).toFixed(2)}/m` : `$${parseFloat(item.unit_price || 0).toFixed(2)}`;
                return `
                <tr>
                  <td>${item.code}</td>
                  <td>${item.color_code}</td>
                  <td>${item.color_name}</td>
                  <td>${qtyDisplay}</td>
                  <td>${priceDisplay}</td>
                  <td>$${parseFloat(item.total_price || 0).toFixed(2)}</td>
                </tr>
                `;
              }).join('')}
              <tr class="total-row">
                <td colspan="5" style="text-align: right;">GRAND TOTAL</td>
                <td>$${grandTotal.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>

          <div class="footer">
            <p>This is a computer-generated document. No signature is required.</p>
            <p>Printed on ${new Date().toLocaleString()}</p>
          </div>
        </body>
      </html>
    `;
    
    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Wait for content to load, then print
    printWindow.onload = () => {
      // Small delay to ensure styles are loaded
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  const handleEditItemInView = (itemIndex) => {
    // Copy the item to edit
    const itemToEdit = viewDetails.items[itemIndex];
    setCurrentItem({
      code: itemToEdit.code,
      color_code: itemToEdit.color_code,
      color_name: itemToEdit.color_name,
      quantity: itemToEdit.quantity,
      unit_price: itemToEdit.unit_price,
      total_price: itemToEdit.total_price
    });
    
    // Remove the item from the list temporarily
    const updatedItems = viewDetails.items.filter((_, idx) => idx !== itemIndex);
    setViewDetails({ ...viewDetails, items: updatedItems });
    
    // Show the item form
    setShowItemForm(true);
  };

  const handleDeleteItemInView = (itemIndex) => {
    if (!window.confirm('Are you sure you want to remove this item?')) return;
    
    const updatedItems = viewDetails.items.filter((_, idx) => idx !== itemIndex);
    setViewDetails({ ...viewDetails, items: updatedItems });
    toast.success('Item removed');
  };

  const handleSaveViewChanges = async () => {
    try {
      const token = localStorage.getItem('token');
      const payload = {
        raw_good_type: viewDetails.raw_good_type,
        items: viewDetails.items,
        expected_delivery_date: viewDetails.expected_delivery_date
      };
      
      await axios.put(`${API}/purchase-orders/${viewDetails.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Purchase Order updated successfully');
      setViewOpen(false);
      fetchPurchaseOrders();
    } catch (error) {
      console.error('Error updating PO:', error);
      toast.error('Failed to update purchase order');
    }
  };

  const handleEditPO = async (po) => {
    // Load PO data into form for editing
    setPoRefNumber(po.po_ref_number);
    setDate(po.date);
    setRawGoodType(po.raw_good_type);
    setFund(po.fund || 'petty_cash');
    setItems(po.items || []);
    setExpectedDeliveryDate(po.expected_delivery_date);
    setErrorMessage('');
    setEditingPO(po); // Set editing mode with PO data
    setOpen(true);
    
    // Fetch codes for the selected module
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data/codes/${po.raw_good_type}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableCodes(response.data);
    } catch (error) {
      console.error('Error fetching codes:', error);
    }
  };

  const getButtonLabel = () => {
    if (!rawGoodType) return 'Add Item';
    if (rawGoodType === 'FABRIC LN') return 'Add Fabric';
    if (rawGoodType === 'TRIMS AND ACCESSORIES LN-Z') return 'Add Trims and Accessories';
    if (rawGoodType === 'PACKAGING LN-Y') return 'Add Packaging';
    return 'Add Item';
  };

  const getTotalAmount = () => {
    return items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0).toFixed(2);
  };

  // Get min date for delivery (tomorrow in Pakistan time)
  const getMinDeliveryDate = () => {
    const now = new Date();
    // Convert to Pakistan time (UTC+5)
    const pakistanTime = new Date(now.getTime() + (5 * 60 * 60 * 1000));
    // Add one day
    pakistanTime.setDate(pakistanTime.getDate() + 1);
    return pakistanTime.toISOString().split('T')[0];
  };

  // Get count for each status
  const getStatusCount = (status) => {
    if (status === 'APPROVED') {
      return purchaseOrders.filter(po => po.status === 'APPROVED' || po.status === 'AUTHORIZED').length;
    } else if (status === 'REJECTED') {
      return purchaseOrders.filter(po => po.status === 'REJECTED' || po.status === 'DENIED').length;
    }
    return purchaseOrders.filter(po => po.status === status).length;
  };

  // Extract numeric part from PO number (e.g., "#00001" -> "1")
  const extractNumeric = (poNumber) => {
    return poNumber.replace(/\D/g, '').replace(/^0+/, '') || '0';
  };

  // Get filtered POs - MASTER SEARCH across all statuses when search term exists
  const getFilteredPOs = () => {
    let filtered = purchaseOrders;
    
    // If search term exists OR category filter is set, search across ALL statuses
    if (searchTerm.trim() !== '' || categoryFilter.trim() !== '') {
      // Apply search term filter if exists
      if (searchTerm.trim() !== '') {
        const searchNumeric = searchTerm.trim();
        filtered = filtered.filter(po => {
          const poNumeric = extractNumeric(po.po_ref_number);
          // Match if the PO number contains the search term
          return poNumeric.includes(searchNumeric) || po.po_ref_number.includes(searchTerm);
        });
      }
      
      // Apply category filter if set
      if (categoryFilter.trim() !== '') {
        filtered = filtered.filter(po => po.raw_good_type === categoryFilter);
      }
    } else {
      // If no search term AND no category filter, filter by selected status only
      if (selectedStatus === 'APPROVED') {
        filtered = filtered.filter(po => po.status === 'APPROVED' || po.status === 'AUTHORIZED');
      } else if (selectedStatus === 'REJECTED') {
        filtered = filtered.filter(po => po.status === 'REJECTED' || po.status === 'DENIED');
      } else {
        filtered = filtered.filter(po => po.status === selectedStatus);
      }
    }
    
    return filtered;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Purchase Orders
              </h1>
              <p className="text-sm text-gray-600">Orders to suppliers</p>
            </div>
          </div>
          {canCreate('purchase_orders') && (
            <Button 
              onClick={handleOpenForm}
              className="bg-black hover:bg-gray-800"
            >
              <Plus className="w-4 h-4 mr-2" /> New Purchase Order
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Master Search and Category Filter */}
        <div className="mb-6 bg-white p-4 rounded-lg border-2 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium mb-2 block">Master Search - Search Across All Statuses</Label>
              <Input
                type="text"
                placeholder="Enter PO Number"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <Label className="text-sm font-medium mb-2 block">Filter by Category</Label>
              <SearchableSelect
                options={[
                  { value: "", label: "All Categories" },
                  { value: "FABRIC LN", label: "Fabric" },
                  { value: "TRIMS AND ACCESSORIES LN-Z", label: "Trims & Accessories" },
                  { value: "PACKAGING LN-Y", label: "Packaging" }
                ]}
                value={categoryFilter}
                onChange={(value) => setCategoryFilter(value)}
                placeholder="All Categories"
              />
            </div>
          </div>
          {(searchTerm || categoryFilter) && (
            <div className="mt-3 flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-black">
                  {searchTerm && `Searching: "${searchTerm}"`}
                  {searchTerm && categoryFilter && ' | '}
                  {categoryFilter && `Category: ${categoryFilter === 'FABRIC LN' ? 'Fabric' : categoryFilter === 'TRIMS AND ACCESSORIES LN-Z' ? 'Trims & Accessories' : 'Packaging'}`}
                </p>
                <p className="text-xs text-gray-600">
                  Found {getFilteredPOs().length} result{getFilteredPOs().length !== 1 ? 's' : ''} across all statuses
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSearchTerm('');
                  setCategoryFilter('');
                }}
                className="text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        {/* Status Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { key: 'PENDING', label: 'Pending', count: getStatusCount('PENDING') },
                { key: 'APPROVED', label: 'Approved/Authorized', count: getStatusCount('APPROVED') },
                { key: 'REJECTED', label: 'Rejected/Denied', count: getStatusCount('REJECTED') },
                { key: 'EXECUTED', label: 'Executed', count: getStatusCount('EXECUTED') }
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setSelectedStatus(tab.key);
                    setSearchTerm('');
                    setCategoryFilter('');
                  }}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${selectedStatus === tab.key
                      ? 'border-black text-black'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  {tab.label} <span className="ml-2 text-gray-500">{tab.count}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Create PO Button */}
        {/* PO Form Dialog */}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingPO ? 'Edit Purchase Order' : 'Create Purchase Order'}</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {errorMessage && (
                <Alert variant="destructive">
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>
              )}

              {/* Auto-generated fields */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PO Ref Number</Label>
                  <Input value={poRefNumber} disabled className="bg-gray-50" />
                </div>
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input value={date} disabled className="bg-gray-50" />
                </div>
              </div>

              {/* Raw Good Type and Fund */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Raw Good Type *</Label>
                  <SearchableSelect
                    options={[
                      { value: 'FABRIC LN', label: 'FABRIC LN' },
                      { value: 'TRIMS AND ACCESSORIES LN-Z', label: 'TRIMS AND ACCESSORIES LN-Z' },
                      { value: 'PACKAGING LN-Y', label: 'PACKAGING LN-Y' }
                    ]}
                    value={rawGoodType}
                    onChange={handleRawGoodTypeChange}
                    placeholder="Select Raw Good Type"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fund *</Label>
                  <SearchableSelect
                    options={fundOptions}
                    value={fund}
                    onChange={setFund}
                    placeholder="Select fund..."
                    searchPlaceholder="Search fund..."
                  />
                </div>
              </div>

              {/* Items Section */}
              {rawGoodType && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label className="text-lg font-semibold">Items</Label>
                    {!showItemForm && (
                      <Button type="button" variant="outline" onClick={() => setShowItemForm(true)}>
                        <Plus className="w-4 h-4 mr-2" /> {getButtonLabel()}
                      </Button>
                    )}
                  </div>

                  {/* Item Form */}
                  {showItemForm && (
                    <div className="border rounded-lg p-4 bg-gray-50 space-y-4">
                      <div className="flex justify-between items-center mb-2">
                        <Label className="font-semibold">Add New Item</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setShowItemForm(false);
                            setCurrentItem({
                              code: '',
                              color_code: '',
                              color_name: '',
                              quantity: '',
                              unit_price: '',
                              total_price: 0
                            });
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Code *</Label>
                          <SearchableSelect
                            options={availableCodes.map(item => ({
                              value: item.code,
                              label: item.code
                            }))}
                            value={currentItem.code}
                            onChange={handleCodeChange}
                            placeholder="Select Code"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Color *</Label>
                          <SearchableSelect
                            options={availableColors.map(color => ({
                              value: color.color_code,
                              label: `${color.color_name} (${color.color_code})`
                            }))}
                            value={currentItem.color_code}
                            onChange={handleColorChange}
                            placeholder="Select Color"
                            disabled={!currentItem.code}
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Quantity *</Label>
                          <Input
                            type="number"
                            step="1"
                            value={currentItem.quantity}
                            onChange={handleQuantityChange}
                            placeholder="Enter value"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Unit Price</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentItem.unit_price}
                            disabled
                            className="bg-gray-50"
                            placeholder="$"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Total Price</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={currentItem.total_price.toFixed(2)}
                            disabled
                            className="bg-gray-50"
                            placeholder="$"
                          />
                        </div>
                      </div>

                      <Button type="button" onClick={handleAddItem} className="w-full">
                        Add to Order
                      </Button>
                    </div>
                  )}

                  {/* Items List */}
                  {items.length > 0 && (
                    <div className="space-y-2">
                      <Label className="font-semibold">Added Items ({items.length})</Label>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Code</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Color</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Quantity</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Unit Price</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Total</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {items.map((item, index) => (
                              <tr key={index}>
                                <td className="px-4 py-2 text-sm">{item.code}</td>
                                <td className="px-4 py-2 text-sm">{item.color_name}</td>
                                <td className="px-4 py-2 text-sm">{item.quantity}</td>
                                <td className="px-4 py-2 text-sm">${parseFloat(item.unit_price).toFixed(2)}</td>
                                <td className="px-4 py-2 text-sm">${parseFloat(item.total_price).toFixed(2)}</td>
                                <td className="px-4 py-2">
                                  <div className="flex gap-0 justify-start">
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => {
                                        // Load item into form for editing
                                        setCurrentItem(item);
                                        setShowItemForm(true);
                                        // Remove from list temporarily
                                        handleRemoveItem(index);
                                      }}
                                      className="h-8 w-8"
                                    >
                                      <Pencil className="w-4 h-4 text-blue-500" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => handleRemoveItem(index)}
                                      className="h-8 w-8"
                                    >
                                      <Trash2 className="w-4 h-4 text-red-500" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-t">
                          <span className="font-semibold">Total Amount:</span>
                          <span className="text-lg font-bold">${getTotalAmount()}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Expected Delivery Date */}
              <div className="space-y-2">
                <Label>Expected Delivery Date *</Label>
                <Input
                  type="date"
                  value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  min={getMinDeliveryDate()}
                  required
                />
                <p className="text-xs text-gray-500">Select a future date for expected delivery</p>
              </div>

              {/* Submit Button */}
              <div className="flex gap-4">
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1">
                  {editingPO ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* View PO Details Modal */}
        <Dialog open={viewOpen} onOpenChange={setViewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Purchase Order Details</DialogTitle>
            </DialogHeader>
            
            {viewDetails && (
              <div className="space-y-6">
                {/* Top Row: PO Details on left, Barcode on right */}
                <div className="flex justify-between items-start">
                  {/* Left side - All fields stacked vertically */}
                  <div className="space-y-4">
                    <div>
                      <Label className="text-gray-600 text-sm">PO Ref Number</Label>
                      <p className="text-sm font-semibold">{viewDetails.po_ref_number}</p>
                    </div>
                    
                    <div>
                      <Label className="text-gray-600 text-sm">Date of Request</Label>
                      <p className="text-sm font-semibold">{viewDetails.date}</p>
                    </div>
                    
                    {/* Display Date of Approval if status is APPROVED or EXECUTED */}
                    {(viewDetails.status === 'APPROVED' || viewDetails.status === 'EXECUTED') && viewDetails.approved_date && (
                      <div>
                        <Label className="text-gray-600 text-sm">Date of Approval</Label>
                        <p className="text-sm font-semibold">
                          {viewDetails.approved_date.split('T')[0]}
                        </p>
                      </div>
                    )}
                    
                    {/* Display Date of Rejection if status is REJECTED */}
                    {viewDetails.status === 'REJECTED' && viewDetails.rejected_date && (
                      <div>
                        <Label className="text-gray-600 text-sm">Date of Rejection</Label>
                        <p className="text-sm font-semibold">
                          {viewDetails.rejected_date.split('T')[0]}
                        </p>
                      </div>
                    )}
                    
                    {/* Display Date of Execution if status is EXECUTED */}
                    {viewDetails.status === 'EXECUTED' && viewDetails.executed_date && (
                      <div>
                        <Label className="text-gray-600 text-sm">Date of Execution</Label>
                        <p className="text-sm font-semibold">
                          {viewDetails.executed_date.split('T')[0]}
                        </p>
                      </div>
                    )}
                    
                    <div>
                      <Label className="text-gray-600 text-sm">Raw Good Type</Label>
                      <p className="text-sm font-semibold">{viewDetails.raw_good_type}</p>
                    </div>
                    
                    {/* Display Vendor Information if available */}
                    {viewDetails.items && viewDetails.items.length > 0 && viewDetails.items[0].local_or_imported && (
                      <>
                        <div>
                          <Label className="text-gray-600 text-sm">Local or Imported</Label>
                          <p className="text-sm font-semibold">{viewDetails.items[0].local_or_imported}</p>
                        </div>
                        <div>
                          <Label className="text-gray-600 text-sm">Country</Label>
                          <p className="text-sm font-semibold">{viewDetails.items[0].country}</p>
                        </div>
                      </>
                    )}
                    
                    <div>
                      <Label className="text-gray-600 text-sm">Expected Date of Delivery</Label>
                      <p className="text-sm font-semibold">{viewDetails.expected_delivery_date}</p>
                    </div>
                    
                    <div>
                      <Label className="text-gray-600 text-sm">Status</Label>
                      <div className="mt-1">
                        <span className={`px-3 py-1 text-sm ${
                          viewDetails.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                          (viewDetails.status === 'APPROVED' || viewDetails.status === 'AUTHORIZED') ? 'bg-green-100 text-green-800' : 
                          (viewDetails.status === 'REJECTED' || viewDetails.status === 'DENIED') ? 'bg-red-100 text-red-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {viewDetails.status}
                        </span>
                      </div>
                    </div>

                    {/* Status Change Dropdown - Only for Pending AND if user has approve permission */}
                    {viewDetails.status === 'PENDING' && canApprove('purchase_orders') && (
                      <div className="border-t pt-3">
                        <Label className="text-sm font-medium text-gray-700 mb-2 block">Change Status</Label>
                        <SearchableSelect
                          options={getFundStatusOptions(viewDetails.fund)}
                          value=""
                          onChange={(value) => {
                            if (value) {
                              handleStatusChangeRequest(value);
                            }
                          }}
                          placeholder="Select new status..."
                          searchPlaceholder="Search status..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Authorization required to change status
                        </p>
                      </div>
                    )}
                  </div>
                  
                  {/* Right side - Barcode */}
                  <div className="flex-shrink-0">
                    {viewDetails.barcode && viewDetails.barcode.image && (
                      <img 
                        src={viewDetails.barcode.image} 
                        alt="PO Barcode" 
                        className="max-w-xs"
                      />
                    )}
                  </div>
                </div>

                {/* Items Table */}
                <div>
                  <Label className="text-gray-600 text-lg font-semibold mb-3 block">Items</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color Code</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Color</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                            {viewDetails.raw_good_type === 'FABRIC LN' ? 'Price per Meter' : 'Unit Price'}
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Price</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {viewDetails.items?.map((item, index) => {
                          const isFabric = viewDetails.raw_good_type === 'FABRIC LN';
                          const qtyDisplay = isFabric ? `${Math.round(item.quantity)} Meters` : `${Math.round(item.quantity)} Units`;
                          const priceDisplay = isFabric ? `$${parseFloat(item.unit_price).toFixed(2)}/m` : `$${parseFloat(item.unit_price).toFixed(2)}`;
                          return (
                            <tr key={index}>
                              <td className="px-4 py-3 text-sm font-medium">{item.code}</td>
                              <td className="px-4 py-3 text-sm">{item.color_code}</td>
                              <td className="px-4 py-3 text-sm">{item.color_name}</td>
                              <td className="px-4 py-3 text-sm">{qtyDisplay}</td>
                              <td className="px-4 py-3 text-sm">{priceDisplay}</td>
                              <td className="px-4 py-3 text-sm">${parseFloat(item.total_price).toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td colSpan="5" className="px-4 py-3 text-right text-sm font-semibold">Grand Total:</td>
                          <td className="px-4 py-3 text-sm font-bold">
                            ${viewDetails.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0).toFixed(2)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Close Button */}
                <div className="flex justify-end mt-6">
                  <Button onClick={() => setViewOpen(false)}>
                    Close
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Execute Confirmation Dialog */}
        <Dialog open={executeDialogOpen} onOpenChange={setExecuteDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Execute Purchase Order</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Are you sure you would like to execute this Purchase Order?
              </p>
              
              {poToExecute && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-semibold">PO Ref: {poToExecute.po_ref_number}</p>
                  <p className="text-sm text-gray-600">Raw Good: {poToExecute.raw_good_type}</p>
                  <p className="text-sm text-gray-600">Items: {poToExecute.items?.length || 0}</p>
                </div>
              )}

              <div className="flex gap-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={cancelExecute} 
                  className="flex-1"
                >
                  No
                </Button>
                <Button 
                  type="button" 
                  onClick={confirmExecute} 
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  Yes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Delete Authorization Dialog */}
        <AuthorizationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          onAuthorized={confirmDelete}
          title="Delete Purchase Order"
          message={poToDelete ? `Enter authorization code to delete PO ${poToDelete.po_ref_number}` : 'Enter authorization code to proceed'}
        />

        {/* Purchase Orders Table */}
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">PO #</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Raw Good</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fund</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Total</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">
                  {searchTerm || categoryFilter ? 'Date' : 
                    selectedStatus === 'PENDING' ? 'Date of Creation' :
                    selectedStatus === 'APPROVED' ? 'Date of Approval' :
                    selectedStatus === 'REJECTED' ? 'Date of Rejection' :
                    selectedStatus === 'EXECUTED' ? 'Date of Execution' : 'Date'}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getFilteredPOs().map((po) => {
                const totalQty = po.items?.reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0) || 0;
                const totalAmount = po.items?.reduce((sum, item) => sum + parseFloat(item.total_price || 0), 0) || 0;
                const isFabric = po.raw_good_type === 'FABRIC LN';
                const qtyDisplay = isFabric ? `${Math.round(totalQty)} Meters` : `${Math.round(totalQty)} Units`;
                
                // Determine which date to display based on status
                let displayDate = '';
                if (po.status === 'PENDING') {
                  displayDate = po.created_at ? new Date(po.created_at).toLocaleDateString() : 'N/A';
                } else if (po.status === 'APPROVED' || po.status === 'AUTHORIZED') {
                  displayDate = po.approved_date ? new Date(po.approved_date).toLocaleDateString() : 'N/A';
                } else if (po.status === 'REJECTED' || po.status === 'DENIED') {
                  displayDate = po.rejected_date ? new Date(po.rejected_date).toLocaleDateString() : 'N/A';
                } else if (po.status === 'EXECUTED') {
                  displayDate = po.executed_date ? new Date(po.executed_date).toLocaleDateString() : 'N/A';
                }
                
                return (
                  <tr key={po.id}>
                    <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                      <button
                        onClick={() => handleViewPO(po)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                      >
                        {po.po_ref_number}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">{po.raw_good_type || 'N/A'}</td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">
                      {fundOptions.find(f => f.value === po.fund)?.label || po.fund || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">{qtyDisplay}</td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">${totalAmount.toFixed(2)}</td>
                    <td className="px-6 py-4 text-sm whitespace-nowrap">{displayDate}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs ${
                        po.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        (po.status === 'APPROVED' || po.status === 'AUTHORIZED') ? 'bg-green-100 text-green-800' :
                        (po.status === 'REJECTED' || po.status === 'DENIED') ? 'bg-red-100 text-red-800' :
                        po.status === 'EXECUTED' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {po.status || 'PENDING'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2 justify-start">
                        {po.status === 'PENDING' && (
                          <>
                            {canEdit('purchase_orders') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleEditPO(po)}
                                className="text-blue-600 border-blue-600 hover:bg-blue-50"
                              >
                                Edit
                              </Button>
                            )}
                            {canDelete('purchase_orders') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(po)}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                        {(po.status === 'APPROVED' || po.status === 'AUTHORIZED') && (
                          <>
                            {hasPermission('purchase_orders_view') && (
                              <>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleExecute(po)}
                                  className="text-green-700 border-green-700 hover:bg-green-50"
                                >
                                  EXECUTE
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handlePrintPO(po)}
                                  className="text-blue-700 border-blue-700 hover:bg-blue-50"
                                >
                                  Print
                                </Button>
                              </>
                            )}
                            {canDelete('purchase_orders') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(po)}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                        {po.status === 'REJECTED' && canDelete('purchase_orders') && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDelete(po)}
                            className="text-red-600 border-red-600 hover:bg-red-50"
                          >
                            Delete
                          </Button>
                        )}
                        {po.status === 'EXECUTED' && (
                          <>
                            {hasPermission('purchase_orders_view') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePrintPO(po)}
                                className="text-blue-700 border-blue-700 hover:bg-blue-50"
                              >
                                Print
                              </Button>
                            )}
                            {canDelete('purchase_orders') && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(po)}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                              >
                                Delete
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {/* Grand Total for EXECUTED POs */}
            {selectedStatus === 'EXECUTED' && getFilteredPOs().length > 0 && (
              <tfoot className="bg-gray-100">
                <tr>
                  <td colSpan="2" className="px-6 py-4"></td>
                  <td className="px-6 py-4 text-right text-sm font-bold">
                    GRAND TOTAL:
                  </td>
                  <td className="px-6 py-4 text-sm font-bold text-green-700">
                    ${getFilteredPOs().reduce((sum, po) => {
                      const totalAmount = po.items?.reduce((itemSum, item) => itemSum + parseFloat(item.total_price || 0), 0) || 0;
                      return sum + totalAmount;
                    }, 0).toFixed(2)}
                  </td>
                  <td colSpan="2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {getFilteredPOs().length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500">No purchase orders found in {selectedStatus} status</p>
          </div>
        )}
      </main>

      {/* Authorization Dialog for Status Change */}
      <AuthorizationDialog
        open={statusAuthDialogOpen}
        onOpenChange={setStatusAuthDialogOpen}
        onAuthorized={confirmStatusChange}
        title="Change Purchase Order Status"
        message={`Enter authorization code to change status to ${pendingStatusChange || ''}`}
        correctCode={viewDetails ? getAuthCodeForFund(viewDetails.fund) : 'poaprden'}
      />
    </div>
  );
}
